import { api } from "../config/api";
import { post, postAllowHttpError } from "../api";

export interface RolesSessionArgs {
  business_id: string | null;
  device_token: string | null;
  session_id: string | null;
}

export interface RoleApiItem {
  role_id?: string;
  business_role_id?: string;
  name: string;
  shortcode: string;
}

export interface GetBusinessRolesResponse {
  success?: boolean;
  data?: { roles?: RoleApiItem[] };
  errors?: string;
}

export interface GetAllActionsResponse {
  success?: boolean;
  data?: {
    permissions?: Array<{ action_shortcode?: string; has_permission?: boolean }>;
    action_categories?: unknown[];
  };
  errors?: string;
}

export interface GetRolePermissionsArgs extends RolesSessionArgs {
  business_role_id: string;
}

export interface GetRolePermissionsResponse {
  success?: boolean;
  data?: {
    role_permissions?: Array<{ action_shortcode?: string; has_permission?: boolean }>;
    role_permissions_category?: unknown[];
  };
  errors?: string;
}

export type SetRolePermissionItem = {
  action_shortcode: string;
  has_permission: boolean;
  action_category_name?: string;
  action_name?: string;
  role_permission_id?: number | string;
  action_id?: number;
  action_category_id?: number;
};

export type SetRolePermissionApiRow = {
  role_permission_id: string | number;
  action_id: number;
  action_name: string;
  action_shortcode: string;
  action_category_id: number;
  action_category_name: string;
  has_permission: boolean;
};

function rolePermissionIdForApi(v: SetRolePermissionItem["role_permission_id"]): string | number {
  if (v === undefined || v === null) return "";
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const t = v.trim();
    if (t === "") return "";
    const n = Number(t);
    if (Number.isFinite(n)) return n;
  }
  return "";
}

function normalizeSetRolePermissionRow(
  item: SetRolePermissionItem
): SetRolePermissionApiRow | null {
  if (!item.action_shortcode || item.action_shortcode === "all") return null;
  const actionId =
    typeof item.action_id === "number" && Number.isFinite(item.action_id)
      ? item.action_id
      : undefined;
  const categoryId =
    typeof item.action_category_id === "number" && Number.isFinite(item.action_category_id)
      ? item.action_category_id
      : undefined;
  if (actionId === undefined || categoryId === undefined) return null;
  return {
    role_permission_id: rolePermissionIdForApi(item.role_permission_id),
    action_id: actionId,
    action_name: item.action_name ?? "",
    action_shortcode: item.action_shortcode,
    action_category_id: categoryId,
    action_category_name: item.action_category_name ?? "",
    has_permission: Boolean(item.has_permission),
  };
}

export interface SetRolePermissionsPayload extends RolesSessionArgs {
  username: string;
  business_role_id: string;
  permissions: SetRolePermissionItem[];
  user_id: string | number;
}

export interface SetRolePermissionsResponse {
  success?: boolean;
  errors?: string;
}

export interface CreateBusinessRolePayload extends RolesSessionArgs {
  name: string;
  shortcode: string;
}

export interface CreateBusinessRoleResponse {
  success?: boolean;
  data?: { role_info?: { business_role_id?: string; role_id?: string; id?: string } };
  errors?: string;
}

export interface CheckBizRoleShortcodePayload extends RolesSessionArgs {
  role_shortcode: string;
}

export interface CheckBizRoleShortcodeResponse {
  success?: boolean;
  message?: string;
  data?: {
    role_info?: {
      is_valid?: boolean;
    };
  };
  errors?: string | string[] | null;
}

function checkBizRoleShortcode(
  payload: CheckBizRoleShortcodePayload
): Promise<CheckBizRoleShortcodeResponse> {
  const { business_id, device_token, session_id, role_shortcode } = payload;
  if (!business_id || !device_token || !session_id) {
    return Promise.reject("business_id, device_token and session_id are required");
  }
  const trimmed = role_shortcode.trim();
  if (!trimmed) {
    return Promise.reject("role_shortcode is required");
  }
  const biz =
    typeof business_id === "string" && business_id.trim() !== ""
      ? Number(business_id)
      : business_id;
  return postAllowHttpError<CheckBizRoleShortcodeResponse>(api.CHECK_BIZ_ROLE_SHORTCODE, {
    business_id: typeof biz === "number" && !Number.isNaN(biz) ? biz : business_id,
    role_shortcode: trimmed,
    device_token,
    session_id,
  });
}

function getBusinessRoles(args: RolesSessionArgs): Promise<GetBusinessRolesResponse> {
  const { business_id, device_token, session_id } = args;
  if (!business_id || !device_token || !session_id) {
    return Promise.reject("business_id, device_token and session_id are required");
  }
  return post<GetBusinessRolesResponse>(api.GET_BUSINESS_ROLES, {
    business_id,
    device_token,
    session_id,
  });
}

function getAllActions(args: RolesSessionArgs): Promise<GetAllActionsResponse> {
  const { business_id, device_token, session_id } = args;
  if (!business_id || !device_token || !session_id) {
    return Promise.reject("business_id, device_token and session_id are required");
  }
  return post<GetAllActionsResponse>(api.GET_ALL_ACTIONS, {
    business_id,
    device_token,
    session_id,
  });
}

function getRolePermissions(args: GetRolePermissionsArgs): Promise<GetRolePermissionsResponse> {
  const { business_id, device_token, session_id, business_role_id } = args;
  if (!business_id || !device_token || !session_id || !business_role_id) {
    return Promise.reject(
      "business_id, device_token, session_id and business_role_id are required"
    );
  }
  return post<GetRolePermissionsResponse>(api.GET_ROLE_PERMISSIONS, {
    business_id,
    device_token,
    session_id,
    business_role_id,
  });
}

function setRolePermissions(
  payload: SetRolePermissionsPayload
): Promise<SetRolePermissionsResponse> {
  const { business_id, device_token, session_id } = payload;
  if (!business_id || !device_token || !session_id) {
    return Promise.reject("business_id, device_token and session_id are required");
  }
  const normalizedPermissions = payload.permissions
    .map(normalizeSetRolePermissionRow)
    .filter((row): row is SetRolePermissionApiRow => row !== null);
  if (normalizedPermissions.length === 0 && payload.permissions.length > 0) {
    return Promise.reject(
      "Each permission needs action_id and action_category_id for this API. Some rows were dropped."
    );
  }
  const body = {
    ...payload,
    permissions: normalizedPermissions,
  };
  return post<SetRolePermissionsResponse>(api.SET_ROLE_PERMISSIONS, body);
}

function createBusinessRole(
  payload: CreateBusinessRolePayload
): Promise<CreateBusinessRoleResponse> {
  const { business_id, device_token, session_id } = payload;
  if (!business_id || !device_token || !session_id) {
    return Promise.reject("business_id, device_token and session_id are required");
  }
  return post<CreateBusinessRoleResponse>(api.CREATE_BUSINESS_ROLE, payload);
}

export const rolesService = {
  getBusinessRoles,
  getAllActions,
  getRolePermissions,
  setRolePermissions,
  createBusinessRole,
  checkBizRoleShortcode,
};
